const CROP_COLOR_MAP = {
  avoine: {
    badgeClassName: "border-[var(--crop-avoine-border)] bg-[var(--crop-avoine-bg)] text-[var(--crop-avoine-text)]",
    countClassName: "bg-white text-[var(--crop-avoine-text)]",
    accentColor: "var(--crop-avoine-text)",
  },
  ble: {
    badgeClassName: "border-[var(--crop-ble-border)] bg-[var(--crop-ble-bg)] text-[var(--crop-ble-text)]",
    countClassName: "bg-white text-[var(--crop-ble-text)]",
    accentColor: "var(--crop-ble-text)",
  },
  canola: {
    badgeClassName: "border-[var(--crop-canola-border)] bg-[var(--crop-canola-bg)] text-[var(--crop-canola-text)]",
    countClassName: "bg-white text-[var(--crop-canola-text)]",
    accentColor: "var(--crop-canola-text)",
  },
  default: {
    badgeClassName: "border-[var(--crop-default-border)] bg-[var(--crop-default-bg)] text-[var(--crop-default-text)]",
    countClassName: "bg-white text-[var(--crop-default-text)]",
    accentColor: "var(--crop-default-text)",
  },
  mais: {
    badgeClassName: "border-[var(--crop-mais-border)] bg-[var(--crop-mais-bg)] text-[var(--crop-mais-text)]",
    countClassName: "bg-white text-[var(--crop-mais-text)]",
    accentColor: "var(--crop-mais-text)",
  },
  orge: {
    badgeClassName: "border-[var(--crop-orge-border)] bg-[var(--crop-orge-bg)] text-[var(--crop-orge-text)]",
    countClassName: "bg-white text-[var(--crop-orge-text)]",
    accentColor: "var(--crop-orge-text)",
  },
  soya: {
    badgeClassName: "border-[var(--crop-soya-border)] bg-[var(--crop-soya-bg)] text-[var(--crop-soya-text)]",
    countClassName: "bg-white text-[var(--crop-soya-text)]",
    accentColor: "var(--crop-soya-text)",
  },
};

const FIXED_PRODUCT_BADGE_CLASS_NAME = "dispatch-product-tone-fixed";
const GENERATED_PRODUCT_BADGE_CLASS_NAME = "dispatch-product-tone-generated";

function normalizeCropName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

const EXACT_CROP_TONE_MAP = {
  avoine: CROP_COLOR_MAP.avoine,
  ble: CROP_COLOR_MAP.ble,
  canola: CROP_COLOR_MAP.canola,
  mais: CROP_COLOR_MAP.mais,
  orge: CROP_COLOR_MAP.orge,
  soya: CROP_COLOR_MAP.soya,
};

const HISTORICAL_FLEET_PRODUCT_COLOR_MAP = {
  // Fumier utilisait le même bleu que Maïs. Le vert provient de l'ancienne
  // palette de repli et constitue la seule association volontairement changée.
  fumier: { background: "#e9f8ee", border: "#bfdcc8", text: "#2f6f4f" },
  laitier: { background: "#f7f3e5", border: "#d9d1b7", text: "#6c6034" },
  porc: { background: "#f3efff", border: "#ddd1ff", text: "#6b4fc3" },
  poulet: { background: "#fdebf3", border: "#f5ccdc", text: "#a13b67" },
  // Dinde recevait par hachage un rose trop proche de Poulet.
  dinde: { background: "#fff1e6", border: "#fed7aa", text: "#c2410c" },
  // Canard, T-Soya et Trituro recevaient par hachage trois verts presque identiques.
  canard: { background: "#e7f6ef", border: "#b5e0cc", text: "#047857" },
  "t-soya": { background: "#eaf0fe", border: "#c3d3fb", text: "#1d4ed8" },
  "t.soya": { background: "#eaf0fe", border: "#c3d3fb", text: "#1d4ed8" },
  trituro: { background: "#f1f7e4", border: "#d3e6b0", text: "#4d7c0f" },
};

function resolveExactCropTone(normalizedName) {
  return EXACT_CROP_TONE_MAP[normalizedName] || null;
}

function resolveMappedCropTone(normalizedName) {
  if (normalizedName.includes("mais") || normalizedName.includes("corn")) {
    return CROP_COLOR_MAP.mais;
  }

  if (normalizedName.includes("ble") || normalizedName.includes("wheat")) {
    return CROP_COLOR_MAP.ble;
  }

  if (normalizedName.includes("soya") || normalizedName.includes("soja") || normalizedName.includes("soy")) {
    return CROP_COLOR_MAP.soya;
  }

  if (normalizedName.includes("orge") || normalizedName.includes("barley")) {
    return CROP_COLOR_MAP.orge;
  }

  if (normalizedName.includes("avoine") || normalizedName.includes("oat")) {
    return CROP_COLOR_MAP.avoine;
  }

  if (normalizedName.includes("canola") || normalizedName.includes("colza") || normalizedName.includes("rapeseed")) {
    return CROP_COLOR_MAP.canola;
  }

  return null;
}

function getStableProductHash(value) {
  let hash = 0x811c9dc5;

  for (const character of value) {
    hash ^= character.codePointAt(0);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  return hash;
}

function buildFixedProductTone({ background, border, text }) {
  const productStyle = {
    "--dispatch-product-fixed-bg": background,
    "--dispatch-product-fixed-border": border,
    "--dispatch-product-fixed-text": text,
  };

  return {
    badgeClassName: FIXED_PRODUCT_BADGE_CLASS_NAME,
    badgeStyle: productStyle,
    countClassName: "bg-white text-[var(--dispatch-product-fixed-text)]",
    countStyle: productStyle,
    accentColor: text,
  };
}

function buildGeneratedProductTone(accentColor) {
  const productStyle = {
    "--dispatch-product-generated-accent": accentColor,
  };

  return {
    badgeClassName: GENERATED_PRODUCT_BADGE_CLASS_NAME,
    badgeStyle: productStyle,
    countClassName: "bg-white text-[var(--dispatch-product-generated-accent)]",
    countStyle: productStyle,
    accentColor,
  };
}

function getGeneratedProductTone(normalizedName) {
  const hash = getStableProductHash(normalizedName);
  const hue = (((hash & 0xffff) / 0x10000) * 360).toFixed(2);
  const chroma = (0.105 + (((hash >>> 16) & 0x7) * 0.008)).toFixed(3);
  const lightness = (0.42 + (((hash >>> 19) & 0x7) * 0.015)).toFixed(3);

  return buildGeneratedProductTone(`oklch(${lightness} ${chroma} ${hue})`);
}

export function getCropTone(cropName) {
  const normalizedName = normalizeCropName(cropName);

  return resolveMappedCropTone(normalizedName) || CROP_COLOR_MAP.default;
}

export function getFleetProductTone(productName) {
  const normalizedName = normalizeCropName(productName);
  const mappedTone = resolveExactCropTone(normalizedName);

  if (mappedTone) {
    return mappedTone;
  }

  if (!normalizedName) {
    return CROP_COLOR_MAP.default;
  }

  const historicalProductColors = HISTORICAL_FLEET_PRODUCT_COLOR_MAP[normalizedName];

  if (historicalProductColors) {
    return buildFixedProductTone(historicalProductColors);
  }

  return getGeneratedProductTone(normalizedName);
}

// Une bande nette par produit, dans l'ordre : couleur pleine pour un seul produit.
export function getFleetProductBandsBackground(productNames = [], angle = "180deg") {
  const colors = productNames.map((productName) => getFleetProductTone(productName).accentColor);
  if (colors.length < 2) return colors[0] || "";
  const step = 100 / colors.length;
  return `linear-gradient(${angle}, ${colors.map((color, index) => `${color} ${index * step}% ${(index + 1) * step}%`).join(", ")})`;
}

export { CROP_COLOR_MAP };
